import { Row } from 'antd';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { SubTitle } from 'ui';
import useLocalStorage from 'utils/hooks';
import Tags from '../../../common/tags/components/tags';
import ActionButton from '../../../ui/action-button';
import useCollapseExpand from '../../../utils/hooks/useCollapseExpand';
import QuestionSection from './question-section';

const QuestionRow = styled(Row)`
  width: 100%;
  margin: 10px 0;
`;

const Description = styled.p`
  line-height: 24px;
  font-size: 16px;
`;

const ConclusionCard = ({
  intl,
  selectedTags,
  allCategoryTags,
  form,
  resetTags,
  setFormDirty,
  categoryInfo,
  ticket,
  isNew,
  questions = [],
  saveSuccess,
}) => {
  const [tags, setTags] = useState(selectedTags);
  const [filteredQuestions, setFilteredQuestions] = useState([...questions]);
  const [isExpertModeEnabled, setIsExpertModeEnabled] = useLocalStorage(
    'isExpertModeEnabled',
    false,
  );
  const [arrow, content] = useCollapseExpand(false);

  useEffect(() => {
    if (questions) {
      setFilteredQuestions(questions?.filter(q => !selectedTags?.find(t => t.questionId === q.id)));
      setTags(selectedTags);
    }
  }, [questions]);

  useEffect(() => {
    if (saveSuccess) {
      setFilteredQuestions(questions?.filter(q => !selectedTags?.find(t => t.questionId === q.id)));
      setTags(selectedTags);
    }
  }, [saveSuccess]);

  useEffect(() => {
    if (resetTags && !isEqual(filteredQuestions, questions)) {
      setFilteredQuestions(questions);
    }
  }, [resetTags]);

  useEffect(() => {
    setTags(selectedTags);
  }, [selectedTags]);

  const handleQuestionAnswer = (e) => {
    const newTag = allCategoryTags?.find(ct => ct.id === e);
    if (newTag) {
      setFormDirty();
      setTags(t => [...t, newTag]);
    }
  };

  const handleQuestionUndo = (e) => {
    setTags(t => t?.filter(tf => tf.id !== e));
  };

  const addTagCallback = (id) => {
    const newTag = allCategoryTags?.find(ct => ct.id === id);
    setFilteredQuestions(state => state?.filter(q => q.id !== newTag?.questionId));
    setTags(state => [...state, newTag]);
  };

  const removeTagCallback = (id, questionId) => {
    const question = questions?.find(q => q.id === questionId);
    if (question) {
      if (filteredQuestions?.find(q => q.id === question.id)) {
        setFilteredQuestions(state =>
          state?.map((q) => {
            if (q.id === question.id) {
              return {
                ...q,
                stopTimer: true,
              };
            }
            return q;
          }),
        );
      } else {
        setFilteredQuestions(state => [...state, question]?.sort((a, b) => a.sort - b.sort));
      }
    }
    setTags(state => state?.filter(t => t.id !== id));
  };

  const allTagsFiltered = allCategoryTags?.filter(t =>
    !questions?.find(q => q.id === t.questionId && q.unknown === t.id),
  ) || [];

  return (
    <>
      <SubTitle>
        <FormattedMessage id='ticket.CONCLUSION_LABEL' />
        <ActionButton
          actions={[{
            text: intl.formatMessage({
              id: `ticket.${isExpertModeEnabled ? 'ASSISTED_MODE' : 'EXPERT_MODE'}`,
            }),
            onClick: () => setIsExpertModeEnabled(s => !s),
          }]}
        />
        {arrow}
      </SubTitle>
      {content(
        <>
          <Row>
            <Description>
              {intl.formatMessage({ id: 'ticket.CONCLUSION_SUBTITLE' })}
            </Description>
          </Row>
          <QuestionRow>
            {!isExpertModeEnabled ?
              <QuestionSection
                intl={intl}
                questions={filteredQuestions}
                setQuestions={setFilteredQuestions}
                handleQuestionAnswer={handleQuestionAnswer}
                handleQuestionUndo={handleQuestionUndo}
                resetTags={resetTags}
              /> :
              null
            }
          </QuestionRow>
          <Row>
            <Tags
              selectedTags={tags}
              filteredTags={allTagsFiltered}
              allTags={allCategoryTags}
              allCategoryTags={allCategoryTags}
              name='conclusionTags'
              form={form}
              setFormDirty={setFormDirty}
              resetTags={resetTags}
              categoryInfo={categoryInfo}
              ticket={ticket}
              isNew={isNew}
              addTagCallback={addTagCallback}
              removeTagCallback={removeTagCallback}
              initialSelectedTags={selectedTags}
              // Don't use custom colors, see https://ant.design/components/tag/
              tagTextColor='volcano'
              width='100%'
              newTagText={intl.formatMessage({ id: 'ticket.NEW_CONCLUSION' })}
            />
          </Row>
        </>,
      )}
    </>
  );
};
ConclusionCard.propTypes = {
  intl: PropTypes.object,
  allCategoryTags: PropTypes.array,
  selectedTags: PropTypes.array,
  form: PropTypes.object,
  setFormDirty: PropTypes.func,
  resetTags: PropTypes.bool,
  categoryInfo: PropTypes.object,
  ticket: PropTypes.object,
  isNew: PropTypes.bool,
  questions: PropTypes.array,
  saveSuccess: PropTypes.bool,
};

export default injectIntl(ConclusionCard);
